import { useState, type CSSProperties, type ReactNode } from 'react';
import { C } from '@ridgewill/brand';
import { useAction, useAsync } from '../lib/useAsync.js';
import { categoryOptions, productOptions, type Category, type SupportApi } from '../lib/support-api.js';
import { Menu, MenuItem, Screen } from '../components/Screen.js';
import { Select } from '../components/Select.js';
import { Modal, ModalActions } from '../components/Modal.js';
import { AsyncBoundary, Badge, Button, EmptyState, Note, OutcomeNote, Panel, Table, Td, inputStyle } from '../components/ui.js';

/**
 * Support > Categories (support#68): the tree tickets are filed under.
 *
 * Any depth (Jon, 2026-10-10), shown as an outline. Support-admins and
 * global-admins add (choosing where in the tree), rename, move and
 * archive, each in a modal (Jon, 2026-10-11: no edit panel at the top);
 * everyone else reads. Nothing is ever deleted: an archived category leaves
 * the pickers and keeps its name on tickets and in the history.
 */
type Editing = { kind: 'add'; parentId: string | null } | { kind: 'edit'; category: Category };

export function SupportCategoriesScreen({ api, tabs }: { api: SupportApi; tabs: ReactNode }) {
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState<Editing | null>(null);
  const { state, reload } = useAsync(() => api.categories(showArchived), [showArchived]);
  const products = useAsync(() => api.products(), []);
  const productChoices = products.state.status === 'ready' ? productOptions(products.state.data.products) : [];
  const productLabel = (slug: string) => productChoices.find((p) => p.value === slug)?.label.replace(/ \(.*\)$/, '') ?? slug;

  return (
    <Screen
      filters={tabs}
      actions={
        <label style={st.check}>
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} /> Show
          archived
        </label>
      }
    >
      <AsyncBoundary state={state}>
        {(d) => (
          <>
            {editing ? (
              <CategoryModal
                api={api}
                categories={d.categories}
                editing={editing}
                productOptions={productChoices}
                onDone={() => {
                  setEditing(null);
                  reload(true);
                }}
                onClose={() => setEditing(null)}
              />
            ) : null}
            <Panel
              title="Categories"
              action={d.canEdit ? <Button onClick={() => setEditing({ kind: 'add', parentId: null })}>Add category</Button> : undefined}
            >
              {d.categories.length === 0 ? (
                <EmptyState title="No categories yet." suggestion={d.canEdit ? 'Add one to start filing tickets.' : undefined} />
              ) : (
                <Table columns={['Category', 'Shows on', 'Tickets, 90 days', '']} actionsLast>
                  {d.categories.map((c) => (
                    <tr
                      key={c.id}
                      // A double click opens the same details as the row's menu (Jon, 2026-10-11).
                      onDoubleClick={d.canEdit ? () => setEditing({ kind: 'edit', category: c }) : undefined}
                      style={d.canEdit ? { cursor: 'default', userSelect: 'none' } : undefined}
                    >
                      <Td>
                        <span style={{ ...st.name, paddingLeft: `${c.depth * 1.4}rem`, ...(c.archivedAt ? st.archived : {}) }}>
                          {c.depth > 0 ? <span style={st.branch}>›</span> : null}
                          {c.name}
                        </span>
                        {c.key ? (
                          <span style={st.badge}>
                            <Badge>Filed automatically</Badge>
                          </span>
                        ) : null}
                        {c.archivedAt ? (
                          <span style={st.badge}>
                            <Badge tone="warn">Archived</Badge>
                          </span>
                        ) : null}
                      </Td>
                      <Td>
                        {c.products.length ? c.products.map(productLabel).join(', ') : <span style={st.muted}>All products</span>}
                      </Td>
                      <Td>{c.ticketsLast90Days}</Td>
                      <Td action>
                        {d.canEdit ? (
                          <Menu label={`Actions for ${c.name}`}>
                            {(close) => (
                              <MenuItem
                                onClick={() => {
                                  close();
                                  setEditing({ kind: 'edit', category: c });
                                }}
                              >
                                Details
                              </MenuItem>
                            )}
                          </Menu>
                        ) : null}
                      </Td>
                    </tr>
                  ))}
                </Table>
              )}
            </Panel>
            {!d.canEdit ? <Note>Only a support admin or global admin can change categories.</Note> : null}
          </>
        )}
      </AsyncBoundary>
    </Screen>
  );
}

/**
 * Add a category (at the top, or inside the one chosen), or edit one: rename,
 * move under another (or to the top), choose where it shows, archive or
 * restore. A category cannot be moved inside itself, so its own branch is left
 * out of the parents offered.
 */
function CategoryModal({
  api,
  categories,
  editing,
  productOptions: products,
  onDone,
  onClose,
}: {
  api: SupportApi;
  categories: Category[];
  editing: Editing;
  productOptions: { value: string; label: string }[];
  onDone: () => void;
  onClose: () => void;
}) {
  const existing = editing.kind === 'edit' ? editing.category : null;
  const [name, setName] = useState(existing?.name ?? '');
  const [parentId, setParentId] = useState(existing ? (existing.parentId ?? '') : (editing.kind === 'add' ? (editing.parentId ?? '') : ''));
  const [product, setProduct] = useState(existing?.products[0] ?? '');
  const action = useAction();

  const ownBranch = new Set<string>();
  if (existing) {
    ownBranch.add(existing.id);
    // Outline order: the branch is the rows after it that are deeper than it.
    const at = categories.findIndex((c) => c.id === existing.id);
    for (let i = at + 1; i < categories.length && categories[i]!.depth > existing.depth; i += 1) ownBranch.add(categories[i]!.id);
  }
  const parents = categoryOptions(categories).filter((o) => !ownBranch.has(o.value));
  const parentName = categories.find((c) => c.id === parentId)?.name;

  const save = () =>
    void action
      .run(async () => {
        const base = { name: name.trim(), parentId: parentId || null };
        if (existing) {
          await api.updateCategory(existing.id, { ...base, products: product ? [product] : [] });
          return `Saved ${base.name}.`;
        }
        // Left unset, a new category takes its parent's products (support's createCategory).
        await api.createCategory({ ...base, ...(product ? { products: [product] } : {}) });
        return `Added ${base.name}.`;
      })
      .then((ok) => {
        if (ok) onDone();
      });

  const archive = (archived: boolean) =>
    void action
      .run(async () => {
        await api.updateCategory(existing!.id, { archived });
        return archived ? `Archived ${existing!.name}.` : `Restored ${existing!.name}.`;
      })
      .then((ok) => {
        if (ok) onDone();
      });

  const title = existing ? existing.name : parentName ? `Add a category inside ${parentName}` : 'Add a category';

  return (
    <Modal
      title={title}
      subtitle={
        existing && !existing.archivedAt
          ? 'Archiving takes this category and everything under it out of the pickers. Tickets keep it.'
          : undefined
      }
      onClose={onClose}
      width={520}
      footer={
        <ModalActions>
          {existing ? (
            <span style={{ marginRight: 'auto' }}>
              {existing.archivedAt ? (
                <Button disabled={action.running} onClick={() => archive(false)}>
                  Restore
                </Button>
              ) : (
                <Button tone="danger" disabled={action.running} onClick={() => archive(true)}>
                  Archive
                </Button>
              )}
            </span>
          ) : null}
          <Button onClick={onClose}>Cancel</Button>
          <Button disabled={action.running || !name.trim()} onClick={save}>
            {existing ? 'Save' : 'Add category'}
          </Button>
        </ModalActions>
      }
    >
      <div style={st.form}>
        <Field label="Name">
          <input
            aria-label="Name"
            autoFocus
            value={name}
            maxLength={80}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && name.trim() && !action.running) save();
            }}
            style={{ ...inputStyle, width: '100%' }}
          />
        </Field>
        <Field label="Inside">
          <Select label="Inside" value={parentId} onChange={setParentId} options={[{ value: '', label: 'Top level' }, ...parents]} />
        </Field>
        <Field label="Shows on">
          <Select
            label="Shows on"
            value={product}
            onChange={setProduct}
            options={[{ value: '', label: existing ? 'All products' : 'Same as the category it is inside' }, ...products]}
          />
        </Field>
      </div>
      <OutcomeNote outcome={action.outcome} />
    </Modal>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '.3rem', fontSize: '.85rem' }}>
      <span style={{ fontWeight: 600, color: C.textMid }}>{label}</span>
      {children}
    </div>
  );
}

const st: Record<string, CSSProperties> = {
  check: { display: 'inline-flex', alignItems: 'center', gap: '.35rem', fontSize: '.85rem', color: C.textMid },
  name: { display: 'inline-flex', alignItems: 'center', gap: '.4rem', fontWeight: 600 },
  branch: { color: C.muted, fontWeight: 400 },
  archived: { color: C.muted, textDecoration: 'line-through' },
  badge: { marginLeft: '.5rem' },
  muted: { color: C.muted },
  form: { display: 'grid', gap: '1rem' },
};
